import { createContext, useContext } from 'react';

import type { InlineCitation } from '@/components/chat/bubbles/references/inlineCitations';
import { numberedChipColorFor } from '@/components/chat/statements/StatementChip';
import { Text } from '@/components/ui/text';
import { useColors } from '@/hooks/useColors';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { getAccessibleHitSlop } from '@/util/accessibleTouchTarget';
import { getPressProps } from '@/util/pressProps';

interface InlineCitationChipProps {
  number: number;
  position: 'left' | 'right';
  onPress: (number: number) => void;
}

/**
 * A reference number placed in the message text after the words that cite
 * it, such as a trial name or a pasted link. It looks like a statement's
 * `[N]` chip, since both are the same footnote number, and opens the
 * reference sheet at that reference.
 */
export function InlineCitationChip({ number, position, onPress }: InlineCitationChipProps) {
  const { colors } = useColors();

  return (
    <Text
      size="xs"
      className="font-bold web:cursor-pointer"
      {...getPressProps(() => onPress(number), getAccessibleHitSlop(24))}
      style={{ color: numberedChipColorFor(position, colors) }}
      {...getAccessibilityProps({
        label: `Reference ${number}`,
        role: 'button',
        hint: 'Shows the full reference',
      })}>
      {`[${number}]`}
    </Text>
  );
}

interface InlineCitationsValue {
  citations: readonly InlineCitation[];
  onOpenCitation: (number: number) => void;
}

/**
 * Hands a plain bubble's inline citations to its message text. GiftedChat
 * renders that text through the chat-wide renderer, which owns link handling
 * and its analytics, so the bubble supplies the citations through context
 * rather than replacing the renderer.
 */
const InlineCitationsContext = createContext<InlineCitationsValue | null>(null);

export const InlineCitationsProvider = InlineCitationsContext.Provider;

export const useInlineCitations = () => useContext(InlineCitationsContext);
